import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { Logo } from "@/components/Logo";
import { scrollToSection } from "@/lib/scroll";

const links = [
  { label: "Services", href: "#services", id: "services" },
  { label: "About Us", href: "#about", id: "about" },
  { label: "Areas", href: "#areas", id: "areas" },
  { label: "Results", href: "#results", id: "results" },
  { label: "Reviews", href: "#reviews", id: "reviews" },
  { label: "Contact", href: "#contact", id: "contact" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const go = (href) => {
    setOpen(false);
    scrollToSection(href);
  };

  return (
    <header data-testid="site-header" className="sticky top-0 z-50 border-b border-slate-200 bg-white/80 backdrop-blur-xl">
      <nav className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-12">
        <button data-testid="nav-logo-button" onClick={() => go("#top")} className="flex items-center gap-3">
          <Logo className="h-10 w-10" />
          <span className="text-left leading-none">
            <span className="block font-serif text-2xl font-semibold tracking-tight text-slate-900">Hyek</span>
            <span className="mt-0.5 block text-[10px] font-bold uppercase tracking-[0.2em] text-teal-700">
              Cleaning Services Ltd
            </span>
          </span>
        </button>

        <div className="hidden items-center gap-9 lg:flex">
          {links.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-link-${l.id}`}
              onClick={() => go(l.href)}
              className="text-sm font-semibold text-slate-600 transition-colors hover:text-teal-700"
            >
              {l.label}
            </button>
          ))}
        </div>

        <div className="hidden items-center gap-6 lg:flex">
          <a
            data-testid="nav-phone-link"
            href="tel:07776714973"
            className="flex items-center gap-2 text-sm font-bold text-slate-900 transition-colors hover:text-teal-700"
          >
            <Phone className="h-4 w-4 text-teal-700" />
            07776 714 973
          </a>
          <button
            data-testid="nav-quote-button"
            onClick={() => go("#contact")}
            className="rounded-full bg-teal-700 px-6 py-2.5 text-sm font-bold text-white shadow-sm transition-transform duration-300 hover:scale-105 hover:bg-teal-600"
          >
            Get a Free Quote
          </button>
        </div>

        <button
          data-testid="nav-mobile-menu-button"
          aria-label="Toggle menu"
          onClick={() => setOpen(!open)}
          className="text-slate-900 lg:hidden"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden border-t border-slate-200 bg-white/95 backdrop-blur-xl lg:hidden"
          >
            <div className="flex flex-col gap-1 px-6 py-4">
              {links.map((l) => (
                <button
                  key={l.href}
                  data-testid={`mobile-nav-link-${l.id}`}
                  onClick={() => go(l.href)}
                  className="border-b border-slate-100 py-3.5 text-left text-base font-semibold text-slate-700"
                >
                  {l.label}
                </button>
              ))}
              <button
                data-testid="mobile-nav-quote-button"
                onClick={() => go("#contact")}
                className="mt-3 rounded-full bg-teal-700 py-3.5 text-sm font-bold text-white"
              >
                Get a Free Quote
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
